// Finn.no -> marketplace bookmarklet.
//
// `collectFinnAds` is the function that runs ON finn.no when the user clicks the
// bookmark. It is serialised with Function.prototype.toString by
// `buildFinnBookmarklet`, so it must stay self-contained: no imports, no closures
// over module scope (the 200-ad cap is therefore inlined), and only syntax Babel
// compiles without external helpers (no spread, no for...of, no destructuring).
// NO COMMENTS INSIDE THE FUNCTION: browsers strip newlines from a javascript: url,
// so a single `//` comment would swallow the rest of the code (seen in Firefox as
// «missing } in compound statement»). tests/finnBookmarklet.test.js guards this.
// Single ad page: Open Graph gives title, text and main image. List pages (Mine
// annonser, search, favourites): one card per ad link.
// It opens our site in a new tab with the collected ads in the url hash; nothing
// is sent to Finn or our API from the bookmark itself. The marketplace grid reads
// `finnImport` from the hash (decodeFinnImport) and opens the import drawer.

export const FINN_IMPORT_PARAM = 'finnImport';
export const FINN_IMPORT_VERSION = 1;
const MAX_ADS = 200;

/* eslint-disable prefer-arrow-callback, func-names, no-restricted-syntax, prefer-template */
export function collectFinnAds(origin) {
    const doc = document;
    const idFrom = function (href) {
        const text = String(href || '');
        const byCode = text.match(/[?&]finnkode=(\d+)/i);
        if (byCode) {
            return byCode[1];
        }
        const byItem = text.match(/\/item\/(\d+)/i);
        if (byItem) {
            return byItem[1];
        }
        const byPath = text.match(/\/(\d{6,})(?:[/?#]|$)/);
        return byPath ? byPath[1] : '';
    };
    const clean = function (value) {
        return String(value || '').replace(/\s+/g, ' ').trim();
    };
    const priceFrom = function (text) {
        const match = clean(text).match(/(\d{1,3}(?:[ \u00a0.]\d{3})+|\d+)\s?(?:[kK][rR](?![a-z\u00e6\u00f8\u00e5])|,-)/);
        if (!match) {
            return null;
        }
        const number = parseInt(match[1].replace(/\D/g, ''), 10);
        return Number.isFinite(number) ? number : null;
    };
    const meta = function (name) {
        const el = doc.querySelector('meta[property="' + name + '"], meta[name="' + name + '"]');
        return el ? clean(el.getAttribute('content')) : '';
    };
    const fullSize = function (src) {
        const match = String(src || '').match(/images\.finncdn\.no\/dynamic\/[^/]+\/([^\s"'?]+)/i);
        return match ? 'https://images.finncdn.no/dynamic/1280w/' + match[1] : src;
    };
    const imageOf = function (img) {
        if (!img) {
            return '';
        }
        const src = img.currentSrc || img.src || img.getAttribute('data-src') || '';
        return /^https?:\/\//i.test(src) ? fullSize(src.split('?')[0]) : '';
    };
    const stripFinn = function (title) {
        return clean(title).replace(/\s*[|–-]\s*FINN(\.no|[ -]torget)?\s*$/i, '').trim();
    };
    const safeParse = function (text) {
        try {
            return JSON.parse(text || '');
        } catch (error) {
            return null;
        }
    };
    const productLd = function () {
        const scripts = doc.querySelectorAll('script[type="application/ld+json"]');
        for (let i = 0; i < scripts.length; i += 1) {
            const parsed = safeParse(scripts[i].textContent);
            const list = Array.isArray(parsed) ? parsed : [parsed];
            for (let k = 0; k < list.length; k += 1) {
                if (list[k] && /Product/i.test(String(list[k]['@type'] || ''))) {
                    return list[k];
                }
            }
        }
        return null;
    };
    const galleryKeys = function (value) {
        const pattern = /images\.finncdn\.no\/dynamic\/(\d+w)\/([^\s"'?,)]+)/gi;
        const text = String(value || '');
        const found = [];
        let match = pattern.exec(text);
        while (match) {
            found.push(match[2]);
            match = pattern.exec(text);
        }
        return found;
    };
    const textOf = function (selector) {
        const el = doc.querySelector(selector);
        return el ? clean(el.textContent) : '';
    };
    const firstTextMatching = function (pattern) {
        const nodes = doc.querySelectorAll('p, span, div, strong, b');
        for (let i = 0; i < nodes.length; i += 1) {
            if (nodes[i].children.length === 0 && pattern.test(clean(nodes[i].textContent))) {
                return clean(nodes[i].textContent);
            }
        }
        return '';
    };
    const ads = [];
    const pageId = /\/item\/\d+|finnkode=\d+/i.test(window.location.href) ? idFrom(window.location.href) : '';

    if (pageId) {
        const product = productLd() || {};
        const offer = Array.isArray(product.offers) ? product.offers[0] : (product.offers || {});
        const ldImage = Array.isArray(product.image) ? product.image[0] : product.image;
        const h1s = doc.querySelectorAll('h1');
        let h1Text = '';
        for (let i = 0; i < h1s.length && !h1Text; i += 1) {
            h1Text = clean(h1s[i].textContent);
        }
        const keys = [];
        const addKeys = function (value) {
            const found = galleryKeys(value);
            for (let k = 0; k < found.length; k += 1) {
                if (keys.indexOf(found[k]) < 0) {
                    keys.push(found[k]);
                }
            }
        };
        addKeys(ldImage);
        addKeys(meta('og:image'));
        const pics = doc.querySelectorAll('img, source, link[rel="preload"], [style*="finncdn"]');
        for (let i = 0; i < pics.length; i += 1) {
            addKeys([pics[i].currentSrc, pics[i].src, pics[i].getAttribute('srcset'), pics[i].getAttribute('imagesrcset'),
                pics[i].getAttribute('href'), pics[i].getAttribute('style')].join(' '));
        }
        const images = [];
        for (let i = 0; i < keys.length && images.length < 20; i += 1) {
            images.push('https://images.finncdn.no/dynamic/1280w/' + keys[i]);
        }
        let price = priceFrom(String(offer.price || '') + ' kr');
        if (price === null) {
            price = priceFrom(firstTextMatching(/^\d[\d \u00a0.]*\s?kr$/i));
        }
        if (price === null) {
            price = priceFrom(doc.body ? doc.body.innerText : '');
        }
        ads.push({
            sourceId: String(product.sku || pageId),
            title: stripFinn(product.name || textOf('[data-testid="object-title"]') || h1Text || meta('og:title') || doc.title),
            description: textOf('[data-testid="description"]') || clean(product.description) || meta('og:description')
                || meta('description'),
            price,
            imageUrls: images,
            externalUrl: window.location.href.split('#')[0],
        });
    } else {
        const links = doc.querySelectorAll('a[href]');
        const seen = {};
        for (let i = 0; i < links.length && ads.length < 200; i += 1) {
            const link = links[i];
            const id = idFrom(link.href);
            if (id && !seen[id]) {
                const parent = link.parentElement || link;
                const card = parent.closest('article, li, [class*="card"], [class*="Card"], [class*="item"]');
                const scope = card || link;
                const heading = scope.querySelector('h2, h3, h4, [class*="title"], [class*="Title"]');
                const title = stripFinn(link.getAttribute('title') || (heading ? heading.textContent : '') || link.textContent);
                const image = imageOf(scope.querySelector('img'));
                if (title && !/^(\d[\d\s.]*\s?kr)$/i.test(title)) {
                    seen[id] = true;
                    ads.push({
                        sourceId: id,
                        title: title.slice(0, 120),
                        description: '',
                        price: priceFrom(scope.textContent),
                        imageUrls: image ? [image] : [],
                        externalUrl: link.href.split('#')[0],
                    });
                }
            }
        }
    }

    if (ads.length === 0) {
        window.alert('Fant ingen annonser på denne siden. Åpne «Mine annonser», et søk eller en enkelt annonse på Finn og prøv igjen.');
        return;
    }
    const json = JSON.stringify({ v: 1, source: window.location.href, ads });
    const base64 = window.btoa(unescape(encodeURIComponent(json)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    window.open(origin + '/index#/marketplace?finnImport=' + base64, '_blank');
}
/* eslint-enable prefer-arrow-callback, func-names, no-restricted-syntax, prefer-template */

// The `javascript:` url for the bookmark, bound to this site's origin. Emitted as
// ONE line: browsers drop newlines from the url anyway, and doing it here keeps
// what the user sees in the copy field identical to what runs.
export function buildFinnBookmarklet(origin) {
    const source = collectFinnAds.toString().replace(/\s*[\r\n]+\s*/g, ' ');
    return `javascript:(${source})(${JSON.stringify(origin)});`;
}

// Reads the hash parameter written by the bookmarklet. Returns the ads array or null.
export function decodeFinnImport(hash) {
    const match = String(hash || '').match(new RegExp(`[?&]${FINN_IMPORT_PARAM}=([^&]+)`));
    if (!match) {
        return null;
    }
    try {
        let base64 = match[1].replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) {
            base64 += '=';
        }
        const json = decodeURIComponent(escape(window.atob(base64)));
        const payload = JSON.parse(json);
        if (!payload || payload.v !== FINN_IMPORT_VERSION || !Array.isArray(payload.ads)) {
            return null;
        }
        return payload.ads.slice(0, MAX_ADS).map((ad, index) => ({
            rowNumber: index + 1,
            sourceId: String(ad.sourceId || ''),
            title: String(ad.title || ''),
            description: String(ad.description || ''),
            price: ad.price === null || ad.price === undefined ? null : ad.price,
            category: '',
            imageUrls: Array.isArray(ad.imageUrls) ? ad.imageUrls : [],
            externalUrl: String(ad.externalUrl || ''),
            isSold: false,
        }));
    } catch (error) {
        return null;
    }
}
